import React, { ChangeEvent, FC, useEffect, useState } from 'react'
import { Cache, I18n } from 'aws-amplify'
import axios from 'axios'
import { Grid, Link, makeStyles, TextField, Theme, Typography } from '@material-ui/core'
import { Autocomplete, createFilterOptions } from '@material-ui/lab'
import { v4 as uuid } from 'uuid'

import { PillButton } from 'components'
import { AuthFlowSteps, EventStages, IUser } from 'types'
import { Countries, ICountry, tokenName, validateEmail } from 'helpers'
import { graphQLMutation } from 'graphql/helpers'
import { registerUser } from 'graphql/customMutations'

import { AboutEventModal } from './AboutEventModal'
import { useHistory } from 'react-router-dom'

interface RegistrationProps {
  userEmail: string
  setAuthState: (state: AuthFlowSteps) => void
  setUserEmail: (email: string) => void
  setUser: (user: IUser) => void
  eventStage?: EventStages
}
interface IPersonalRegErrors {
  firstName: string
  lastName: string
  company?: string
  country?: string
  email: string
}

export const Registration: FC<RegistrationProps> = ({ userEmail, setAuthState, setUserEmail, eventStage }) => {
  const classes = useStyles()
  const history = useHistory()
  const userId = uuid()
  const eventUser: IUser = {
    id: userId,
    firstName: '',
    lastName: '',
    email: userEmail,
    title: '',
    company: '',
    country: 'United States'
  }

  const postEventUser: IUser = {
    id: userId,
    firstName: '',
    lastName: '',
    email: userEmail
  }

  const initialUser: IUser = eventStage === EventStages.POSTEVENT ? postEventUser : eventUser

  const eventErrors: IPersonalRegErrors = {
    firstName: '',
    lastName: '',
    company: '',
    country: '',
    email: ''
  }
  const postEventErrors: IPersonalRegErrors = {
    firstName: '',
    lastName: '',
    email: ''
  }
  const initialPersonalErrors: IPersonalRegErrors = eventStage === EventStages.POSTEVENT ? postEventErrors : eventErrors

  const [userInfo, setUserInfo] = useState<IUser>(initialUser)
  const [loading, setLoading] = useState<boolean>(false)
  const [personalErrors, setPersonalErrors] = useState<IPersonalRegErrors>(initialPersonalErrors)
  const [aboutModalOpen, setAboutModalOpen] = useState<boolean>(false)

  useEffect(() => {
    if (userEmail) {
      setUserInfo({ ...userInfo, email: userEmail })
    }
    // eslint-disable-next-line
  }, [userEmail])

  useEffect(() => {
    return () => {
      setUserInfo(initialUser)
    }
    // eslint-disable-next-line
  }, [])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e
    setUserInfo({
      ...userInfo,
      [target.name]: target.value
    })
  }

  const createNewUser = async () => {
    const lowerCaseEmail: string = userInfo.email?.toLowerCase() || ''
    try {
      const { data } = await graphQLMutation(registerUser, {
        ...userInfo,
        email: lowerCaseEmail
      })
      if (typeof data === 'string' && data.toLowerCase() === 'email already registered') {
        setPersonalErrors({ ...personalErrors, email: I18n.get('emailAlreadyRegistered') })
        return false
      }
      setUserEmail(lowerCaseEmail)
      return true
    } catch (e) {
      console.log(e)
      return false
    }
  }

  const sendIntegrateData = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const utmCampaignParams = {
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        utm_term: urlParams.get('utm_term') || '',
        utm_content: urlParams.get('utm_content') || ''
      }

      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'application/json'
        },
        url: 'https://f1chjtarbg.execute-api.us-east-1.amazonaws.com/prod/integrate',
        data: {
          user: {
            ...userInfo,
            ...utmCampaignParams,
            createdAt: new Date().getTime(),
            status: 'Registered'
          },
          event: 'publicsector'
        }
      })
      // Remove URL params
      window.history.replaceState({}, document.title, '/')
      return response.data
    } catch (error) {
      console.log(error)
    }
  }

  const personalFormHasErrors = (): boolean => {
    const requiredText = I18n.get('requiredField')
    const errorObj: IPersonalRegErrors = {
      firstName: !userInfo.firstName ? requiredText : '',
      lastName: !userInfo.lastName ? requiredText : '',
      company: !userInfo.company && eventStage !== EventStages.POSTEVENT ? requiredText : '',
      country: !userInfo.country && eventStage !== EventStages.POSTEVENT ? requiredText : '',
      email: !userInfo.email ? requiredText : !validateEmail(userInfo.email) ? I18n.get('invalidEmail') : ''
    }
    const hasErrors = Object.keys(errorObj).some(key => errorObj[key] !== '')

    if (hasErrors) {
      setPersonalErrors(errorObj)
      return true
    }

    return false
  }

  const submitUser = async () => {
    const hasErrors = personalFormHasErrors()
    if (!hasErrors) {
      setLoading(true)
      setUserInfo({
        ...initialUser,
        ...userInfo
      })
      try {
        const created = await createNewUser()
        await sendIntegrateData()
        if (created) {
          const lowerCaseEmail = userInfo.email?.toLowerCase()
          Cache.setItem(tokenName, { email: userInfo.email?.toLowerCase() })
          if (eventStage !== EventStages.REGISTRATION || lowerCaseEmail?.includes('@mvrk.co')) {
            return history.push('/event')
          } else {
            return setAuthState(AuthFlowSteps.ThankYou)
          }
        }
        return
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      submitUser()
    }
  }

  // this reg form is split into two sections, personal and company
  return (
    <Grid container direction='column' justify='center'>
      <Grid item>
        <Typography variant='h2' className={classes.heading} dangerouslySetInnerHTML={{ __html: I18n.get('joinUs') }} />
      </Grid>

      <Grid item container alignItems='center'>
        <Typography component='p' display='inline'>
          <span>{I18n.get('alreadyHaveAnAccount')}</span>
          <Link component='button' className={classes.inlineButton} onClick={() => setAuthState(AuthFlowSteps.SignIn)}>
            {I18n.get('signIn')}
          </Link>
        </Typography>
      </Grid>

      <Grid item container spacing={3} className={classes.formFieldsContainer}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            className={classes.input}
            id='email'
            error={!!personalErrors.email}
            helperText={personalErrors.email}
            onFocus={() => setPersonalErrors({ ...personalErrors, email: '' })}
            label={I18n.get('email')}
            type='email'
            name='email'
            variant='outlined'
            onChange={handleChange}
            onKeyPress={handleKeyPress}
          />
        </Grid>

        <Grid item container xs={12} className={classes.emailTooltip} style={{ paddingTop: 0 }}>
          <Typography
            dangerouslySetInnerHTML={{
              __html:
                eventStage === EventStages.POSTEVENT ? I18n.get('emailTooltipPostEvent') : I18n.get('emailTooltip')
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            variant='outlined'
            label={I18n.get('firstName')}
            error={!!personalErrors.firstName}
            helperText={personalErrors.firstName}
            onFocus={() => setPersonalErrors({ ...personalErrors, firstName: '' })}
            fullWidth
            className={classes.input}
            type='text'
            name='firstName'
            required
            onChange={handleChange}
            onKeyPress={handleKeyPress}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            variant='outlined'
            label={I18n.get('lastName')}
            error={!!personalErrors.lastName}
            helperText={personalErrors.lastName}
            onFocus={() => setPersonalErrors({ ...personalErrors, lastName: '' })}
            fullWidth
            className={classes.input}
            type='text'
            name='lastName'
            required
            onChange={handleChange}
            onKeyPress={handleKeyPress}
          />
        </Grid>

        {eventStage !== EventStages.POSTEVENT && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                variant='outlined'
                label={I18n.get('titlePosition')}
                fullWidth
                className={classes.input}
                type='text'
                name='title'
                onChange={handleChange}
                onKeyPress={handleKeyPress}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                variant='outlined'
                required
                error={!!personalErrors.company}
                helperText={personalErrors.company}
                onFocus={() => setPersonalErrors({ ...personalErrors, company: '' })}
                label={I18n.get('company')}
                fullWidth
                className={classes.input}
                type='text'
                name='company'
                onChange={handleChange}
                onKeyPress={handleKeyPress}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                id='state-select'
                autoHighlight
                autoSelect
                options={Countries}
                filterOptions={createFilterOptions({ matchFrom: 'start', stringify: option => option.name })}
                getOptionLabel={option => option.name}
                onChange={(_: any, newValue: ICountry | null) =>
                  setUserInfo({ ...userInfo, country: newValue ? newValue.name : '' })
                }
                defaultValue={Countries.find(country => country.code === 'US')}
                renderOption={option => option.name}
                renderInput={params => (
                  <TextField
                    {...params}
                    className={classes.input}
                    error={!!personalErrors.country}
                    helperText={personalErrors.country}
                    onFocus={() => setPersonalErrors({ ...personalErrors, country: '' })}
                    required
                    fullWidth
                    label={I18n.get('country')}
                    variant='outlined'
                  ></TextField>
                )}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Grid item container xs={12} className={classes.review}>
        <Typography paragraph>
          Review the{' '}
          <Link
            component='button'
            className={`${classes.inlineButton} noMargin`}
            onClick={() => setAboutModalOpen(true)}
          >
            event details and agenda here
          </Link>
          .
        </Typography>
        <AboutEventModal open={aboutModalOpen} onClose={() => setAboutModalOpen(false)} />
      </Grid>

      {/* advance to the next section */}
      <Grid item>
        <PillButton type='submit' onClick={submitUser} loading={loading} className={classes.button} solid>
          {I18n.get('finish')}
        </PillButton>
      </Grid>
    </Grid>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    minWidth: 165,
    marginTop: '1rem',
    [theme.breakpoints.down('md')]: {
      marginBottom: '1rem'
    }
  },
  heading: {
    fontWeight: 700,
    fontSize: '2rem',
    lineHeight: '2rem',
    marginBottom: 32
  },
  input: {
    color: '#000',
    borderRadius: 0,
    '& .MuiInputBase-root': {
      backgroundColor: '#fff'
    },
    '& .MuiInputBase-root .MuiAutocomplete-input': {
      border: 'none',
      padding: '3px 7px'
    },
    '& fieldset': {
      borderRadius: 0,
      borderColor: '#dadada',
      borderBottomColor: '#000'
    }
  },
  innerInput: {
    display: 'grid',
    minHeight: '42px',
    border: 0
  },
  inlineButton: {
    color: '#000',
    margin: '0 .5rem',
    fontFamily: 'Verizon-Regular',
    fontWeight: 'normal',
    textDecoration: 'underline',
    verticalAlign: 'initial',
    '&.noMargin': {
      margin: 0
    }
  },
  spaceAbove: {
    marginTop: '2rem'
  },
  inlinePillButton: {
    minWidth: 165,
    marginTop: '0 !important',
    marginBottom: '0 !important',

    [theme.breakpoints.down('md')]: {
      marginBottom: '1rem',
      backgroundColor: '#fff !important'
    },
    [theme.breakpoints.down('xs')]: {
      padding: '0 2rem !important'
    }
  },
  review: {
    marginTop: '1rem'
  },
  emailTooltip: {
    color: '#747676',
    paddingTop: 0,
    paddingBottom: 0,
    '& p': {
      fontSize: '14px',
      lineHeight: '16px'
    }
  },
  formFieldsContainer: {
    marginTop: '1rem'
  }
}))
